'use client'

import * as React from 'react'
import { useParams } from 'next/navigation'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useIsLoggedInQuery } from 'auth/hooks'
import { Button } from '@repo/design-system-cf/components/button'
import { IconAdd } from '@repo/design-system-cf/components/icons'
import { SessionStatus } from '@repo/rpc-types-creativefabrica-common-v1/imageprocessor/v1'
import { Link } from '@repo/web-ui/components/link'
import { AppRoutes } from '@repo/web-utils/app-routes'
import {
  GetImagesQueryRootKey,
  ListQueryRootKey,
  getImagesFn,
  useListInfiniteQuery,
} from '@web/gen/service/creativefabrica/backgroundremover/v2'
import { tw } from 'utils/classnames'
import { ToolFrame, toolRailClassName, toolRailThumbClassName } from '../../_components/tool-frame'
import { HistoryThumbPendingOverlay } from '../../_components/tool-session-rail'
import { useRefreshSessionListOnDone } from '../../_components/use-refresh-session-list'
import { marketplaceBackgroundRemoverListRequest } from './list-request'
import { getSessionImagesVariables } from './session-images-query'

// `bgRemover` is marked deprecated in the schema in favour of `imageProcessorBgRemover`,
// but services-notifications only registered mappers for the new name on the upscaler.
// Subscribing under the new name returns invalid_argument "unhandled event".
const backgroundRemoverSessionDoneEvent = {
  studio: {
    bgRemover: {
      sessionsCompleted: {},
    },
  },
}

const SESSION_LIST_STALE_TIME_MS = 5 * 60 * 1000
const PENDING_LIST_REFETCH_MS = 1000

interface HistorySession {
  id?: string
  thumbnailUrl?: string
  images?: number
  status?: SessionStatus
}

export function isHistorySessionPending(session: HistorySession) {
  if (session.status === SessionStatus.CREATED || session.status === SessionStatus.REQUESTED) {
    return true
  }
  return session.status === SessionStatus.COMPLETED && !session.thumbnailUrl
}

export function historyListRefetchInterval(sessions: HistorySession[]) {
  return sessions.some(isHistorySessionPending) ? PENDING_LIST_REFETCH_MS : false
}

const HISTORY_RAIL_END_GAP_PX = 48

export function isHistoryRailNearEnd(rail: {
  scrollHeight: number
  scrollTop: number
  clientHeight: number
  scrollWidth: number
  scrollLeft: number
  clientWidth: number
}) {
  const overflowY = rail.scrollHeight - rail.clientHeight
  const overflowX = rail.scrollWidth - rail.clientWidth
  if (overflowY <= 1 && overflowX <= 1) {
    return true
  }
  if (overflowY >= overflowX) {
    return overflowY - rail.scrollTop < HISTORY_RAIL_END_GAP_PX
  }
  return overflowX - rail.scrollLeft < HISTORY_RAIL_END_GAP_PX
}

function prefetchSessionImages(queryClient: ReturnType<typeof useQueryClient>, sessionId: string) {
  const variables = getSessionImagesVariables(sessionId)
  const { pagination: _, ...otherVariables } = variables
  void queryClient.prefetchInfiniteQuery({
    // oxlint-disable-next-line oxlint-plugin-query/exhaustive-deps -- generated infinite key hashes otherVariables (request minus pagination)
    queryKey: [GetImagesQueryRootKey, JSON.stringify(otherVariables)],
    initialPageParam: undefined as string | undefined,
    queryFn: () => getImagesFn(variables),
    staleTime: SESSION_LIST_STALE_TIME_MS,
  })
}

export function SessionRail() {
  const t = useTranslations('pages.background-remover')
  const params = useParams<{ id?: string }>()
  const activeSessionId = params?.id
  const queryClient = useQueryClient()
  const { data: isLoggedIn } = useIsLoggedInQuery()
  useRefreshSessionListOnDone(backgroundRemoverSessionDoneEvent, ListQueryRootKey)
  const railRef = React.useRef<HTMLDivElement>(null)
  const listQuery = useListInfiniteQuery(marketplaceBackgroundRemoverListRequest, {
    enabled: Boolean(isLoggedIn),
    staleTime: SESSION_LIST_STALE_TIME_MS,
    refetchInterval: query =>
      historyListRefetchInterval(
        query.state.data?.pages.flatMap(page => page.sessions ?? []) ?? [],
      ),
  })

  const sessions =
    listQuery.data?.pages.flatMap(page => page.sessions ?? []).filter(session => session.id) ?? []
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = listQuery

  const loadMoreIfNearEnd = React.useCallback(() => {
    const rail = railRef.current
    if (!rail || !hasNextPage || isFetchingNextPage) {
      return
    }
    if (isHistoryRailNearEnd(rail)) {
      void fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  React.useEffect(() => {
    loadMoreIfNearEnd()
  }, [loadMoreIfNearEnd, sessions.length])

  const isHome = !activeSessionId

  return (
    <nav aria-label={t('history')} className={tw(toolRailClassName, 'md:overflow-hidden')}>
      {isHome ? (
        <Button
          variant="primary"
          size="small"
          disabled
          className="shrink-0"
          aria-label={t('new_session')}
        >
          <IconAdd className="h-5 w-5" />
        </Button>
      ) : (
        <Button variant="primary" size="small" className="shrink-0" asChild>
          <Link
            href={AppRoutes.TOOLS_BACKGROUND_REMOVER}
            component="Link"
            aria-label={t('new_session')}
          >
            <IconAdd className="h-5 w-5" />
          </Link>
        </Button>
      )}

      <div
        ref={railRef}
        onScroll={loadMoreIfNearEnd}
        className="scroller-xs flex h-full min-w-0 flex-1 flex-row items-center gap-2 overflow-x-auto md:h-auto md:min-h-0 md:w-full md:flex-col md:overflow-x-hidden md:overflow-y-auto"
      >
        {sessions.map((session, index) => {
          const href = `${AppRoutes.TOOLS_BACKGROUND_REMOVER}${session.id}`
          const isActive = session.id === activeSessionId
          return (
            <Link
              key={session.id}
              href={href}
              component="Link"
              aria-current={isActive ? 'page' : undefined}
              onMouseEnter={() => {
                if (session.id) {
                  prefetchSessionImages(queryClient, session.id)
                }
              }}
              className={toolRailThumbClassName(isActive)}
            >
              {session.thumbnailUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={session.thumbnailUrl}
                  alt=""
                  width={48}
                  height={48}
                  decoding="async"
                  loading={index < 8 ? 'eager' : 'lazy'}
                  className="size-full object-cover"
                />
              ) : (
                <span className="block size-full bg-cf-grey-100" />
              )}
              {isHistorySessionPending(session) ? (
                <HistoryThumbPendingOverlay label={t('processing')} />
              ) : null}
              {session.images && session.images > 1 ? (
                <span className="absolute bottom-0.5 right-0.5 rounded bg-cf-grey-1000 px-1 text-[10px] font-semibold text-cf-grey-0">
                  {session.images}
                </span>
              ) : null}
            </Link>
          )
        })}
      </div>
    </nav>
  )
}

export function BackgroundRemoverFrame({ children }: { children: React.ReactNode }) {
  return <ToolFrame rail={<SessionRail />}>{children}</ToolFrame>
}
